import type { Match, TeamId } from '@/core/schemas';

/** A finished match reduced to what standings and brackets need. */
export interface DecidedMatch {
  readonly homeTeamId: TeamId;
  readonly awayTeamId: TeamId;
  readonly legs: { readonly home: number; readonly away: number };
  readonly sets: { readonly home: number; readonly away: number } | null;
  /** `null` means a draw, which an even `bestOf` allows. */
  readonly winnerTeamId: TeamId | null;
}

/**
 * Extracts the usable result from a match, or `null` if there is none yet.
 *
 * Deliberately strict: a match counts only when it is completed, carries a
 * result, and both sides are actual teams. Everything downstream can then work
 * without defensive checks, and a match in conflict never quietly influences a
 * table.
 */
export function toDecidedMatch(match: Match): DecidedMatch | null {
  if (match.status !== 'completed' || match.result === null) {
    return null;
  }

  if (match.home.kind !== 'team' || match.away.kind !== 'team') {
    return null;
  }

  return {
    homeTeamId: match.home.teamId,
    awayTeamId: match.away.teamId,
    legs: match.result.score.legs,
    sets: match.result.score.sets ?? null,
    winnerTeamId: match.result.winnerTeamId,
  };
}

/** Winner of a decided match, including walkovers where one side was a bye. */
export function winnerOf(match: Match): TeamId | null {
  if (match.status !== 'completed' || match.result === null) {
    return null;
  }
  return match.result.winnerTeamId;
}
